import React, { useEffect, useState } from 'react';
import useFetch from 'use-http';
import moment from 'moment';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import TSDILandman from 'src/models/personnel/TSDILandman';
import ActiveContractor from 'src/models/personnel/ActiveContractor';

interface IActiveContractorsTable {
    selectedPersonnel?: TSDILandman;
    showToast: (message: string, color?: string, persistent?: boolean) => void;
    setSelectedPersonnel: (personnel: TSDILandman) => void;
}

const ActiveContractorsTable = (props: IActiveContractorsTable) => {
    const { get, response } = useFetch();
    const [activeContractors, setActiveContractors] = useState<Array<ActiveContractor>>();
    const [isLoading, setIsLoading] = useState<boolean>();
    const [selectedId, setSelectedId] = useState<number>(undefined);

    useEffect(() => {
        initializeData();
    }, []);

    useEffect(() => {
        if (selectedId !== undefined && selectedId !== null) {
            handleSelectRow(selectedId);
        }
    }, [selectedId]);

    const initializeData = async () => {
        getActiveContractors();
    };

    const getActiveContractors = async () => {
        setIsLoading(true);
        setActiveContractors(null);

        let results = await get(`/v1.0/landmen/benefits/active-contractors`);

        if (response.ok && results.length > 0) {
            setIsLoading(false);

            results.forEach((emp: ActiveContractor) => {
                emp.hireDate = emp.hireDate ? (moment(emp.hireDate).format('MM-DD-YYYY') as unknown as Date) : null;
                emp.rehireDate = emp.rehireDate ? (moment(emp.rehireDate).format('MM-DD-YYYY') as unknown as Date) : null;
                emp.firstWorked = emp.firstWorked ? (moment(emp.firstWorked).format('MM-DD-YYYY') as unknown as Date) : null;
                emp.lastWorked = emp.lastWorked ? (moment(emp.lastWorked).format('MM-DD-YYYY') as unknown as Date) : null;
            });

            setActiveContractors(results);
        } else {
            console.log(response);
            props.showToast(`Unable to fetch active contractors: ${response}`, `rgba(255,0,0,0.5)`);
        }

        setIsLoading(false);
    };

    // select personnel from row
    const handleSelectRow = async (id: number) => {
        let result = await get(`v1.0/landmen/personnel/${id}/get-by-tsd-no`);

        if (response.ok && result) {
            props.setSelectedPersonnel(result);
        } else {
            console.log('unable to find personnel');
        }
    };

    return (
        <>
            <div style={{ overflowY: 'scroll', maxHeight: '55rem' }}>
                {isLoading && (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '50vh',
                            width: '100%',
                        }}
                    >
                        <LoadingSpinner />
                    </div>
                )}

                {activeContractors?.length > 0 && <div>
                    <table style={{ width: '100%' }}>
                        <thead>
                            <tr style={{ background: '#d6d6d6' }}>
                                <th style={{ width: '3%' }} className="sticky-header">Emp No</th>
                                <th style={{ width: '8%' }} className="sticky-header">Last</th>
                                <th style={{ width: '8%' }} className="sticky-header">First</th>
                                <th style={{ width: '4%' }} className="sticky-header">Middle</th>
                                <th style={{ width: '8%' }} className="sticky-header">AKA Name</th>
                                <th style={{ width: '8%' }} className="sticky-header">Corp. Name</th>
                                <th style={{ width: '8%' }} className="sticky-header">City</th>
                                <th style={{ width: '2%' }} className="sticky-header">State</th>
                                <th style={{ width: '8%' }} className="sticky-header">Hire Date</th>
                                <th style={{ width: '8%' }} className="sticky-header">Rehire Date</th>
                                <th style={{ width: '4%' }} className="sticky-header">Pay Rate</th>
                                <th style={{ width: '2%' }} className="sticky-header">Unit</th>
                                <th style={{ width: '8%' }} className="sticky-header">First Work</th>
                                <th style={{ width: '8%' }} className="sticky-header">Last Work</th>
                                <th style={{ width: '2%' }} className="sticky-header">Omit</th>
                                <th style={{ width: '16%' }} className="sticky-header">States</th>
                            </tr>
                        </thead>
                        {activeContractors?.map((emp: ActiveContractor, index: number) => {
                            return (
                                <tbody key={index}>
                                    <tr
                                        key={`p-${index}`}
                                        onClick={() => setSelectedId(emp.tsdNo)}
                                        style={{ backgroundColor: props.selectedPersonnel?.tsdNo === emp?.tsdNo ? 'yellow' : undefined }}
                                    >
                                        <td style={{ width: '3%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.tsdNo}
                                        </td>
                                        <td style={{ width: '8%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.lastName}
                                        </td>
                                        <td style={{ width: '8%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.firstName}
                                        </td>
                                        <td style={{ width: '3%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.middleName}
                                        </td>
                                        <td style={{ width: '8%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.akaName}
                                        </td>
                                        <td style={{ width: '8%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.corporationName}
                                        </td>
                                        <td style={{ width: '8%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.city}
                                        </td>
                                        <td style={{ width: '2%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.state}
                                        </td>
                                        <td style={{ width: '8%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.hireDate}
                                        </td>
                                        <td style={{ width: '8%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.rehireDate}
                                        </td>
                                        <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                            {new Intl.NumberFormat('en-US', {
                                                style: 'currency',
                                                currency: 'USD',
                                            }).format(emp?.payRate)}
                                        </td>
                                        <td style={{ width: '2%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.payUnit}
                                        </td>
                                        <td style={{ width: '8%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.firstWorked}
                                        </td>
                                        <td style={{ width: '8%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.lastWorked}
                                        </td>
                                        <td style={{ width: '2%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.omitTeServices}
                                        </td>
                                        <td style={{ width: '16%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {emp?.states}
                                        </td>
                                    </tr>
                                </tbody>
                            );
                        })}
                    </table>
                </div>}
            </div>
        </>
    );
};

export default ActiveContractorsTable;



// WEBPACK FOOTER //
// ./src/components/personnel/components/ActiveContractors.tsx